/**
 * Sticky mobile add-to-cart bar for PDPs.
 *
 * Behaviour:
 *  - Hidden until the main add-to-cart button leaves the viewport.
 *  - Shows the current selected variant + price (kept in sync with the
 *    main variant-picker's radio inputs via a MutationObserver on the
 *    picker's <script type="application/json"> state block).
 *  - Selecting a variant in the sticky bar's <select> updates the
 *    matching radio input in the main picker so the whole page stays
 *    consistent.
 *  - Add-to-cart uses the same route pattern as the main product form
 *    and dispatches CartAddEvent so the cart drawer refreshes.
 */

import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

class StickyAtcBar extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.priceEl = this.querySelector('[data-sticky-price]');
    this.selectEl = this.querySelector('[data-sticky-variant-select]');
    this.ctaEl = this.querySelector('[data-sticky-atc]');

    this._observeMainAtc();
    this._bindVariantSync();
    this._bindAdd();
  }

  /* ---------------- visibility ---------------- */

  _observeMainAtc() {
    // Find the primary Add-to-cart button on the page.
    const mainAtc =
      document.querySelector(
        'add-to-cart-component button[name="add"], product-form-component button[name="add"], main button[name="add"]'
      );
    if (!mainAtc) return;

    this.removeAttribute('hidden');

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.removeAttribute('data-visible');
          } else {
            this.setAttribute('data-visible', '');
          }
        });
      },
      { threshold: 0, rootMargin: '0px' }
    );
    io.observe(mainAtc);
    this._io = io;
  }

  /* ---------------- variant sync ---------------- */

  _bindVariantSync() {
    // Read initial variant from the main picker (in case a variant is
    // pre-selected via URL) and reflect price/state.
    this._syncFromPicker();

    // Watch the main variant picker for changes.
    const picker = document.querySelector('variant-picker');
    if (picker) {
      picker.addEventListener('change', () => this._syncFromPicker());
      // Also observe the JSON state block which the theme updates
      // when a variant is selected.
      const stateEl = picker.querySelector('script[type="application/json"]');
      if (stateEl) {
        const mo = new MutationObserver(() => this._syncFromPicker());
        mo.observe(stateEl, { characterData: true, childList: true, subtree: true });
        this._mo = mo;
      }
    }

    // When the bar's own select changes, mirror the choice back to the
    // main picker so the whole page stays consistent.
    if (this.selectEl) {
      this.selectEl.addEventListener('change', (e) => {
        const variantId = e.target.value;
        if (!variantId) return;
        this._reflectToMainPicker(variantId);
        this._setVariant(variantId);
      });
    }
  }

  _syncFromPicker() {
    const picker = document.querySelector('variant-picker');
    if (!picker) return;

    // Prefer the JSON state block if present (it's the source of truth).
    const stateEl = picker.querySelector('script[type="application/json"]');
    let variant = null;
    if (stateEl) {
      try {
        variant = JSON.parse(stateEl.textContent);
      } catch (_) {}
    }

    // Fallback: derive from checked radios. Only counts as "selected"
    // when every option group has a checked input.
    if (!variant) {
      const groups = picker.querySelectorAll('.variant-option');
      let allPicked = true;
      groups.forEach((g) => {
        const radios = g.querySelectorAll('input[type="radio"]');
        if (radios.length && !Array.from(radios).some((r) => r.checked)) {
          allPicked = false;
        }
      });
      if (!allPicked) {
        this._setVariant(null);
        return;
      }
      // We can't easily reconstruct the variant object without the JSON
      // block, so just enable the CTA and let the server pick.
      const checked = picker.querySelector('input[type="radio"][checked], input[type="radio"]:checked');
      if (checked && checked.dataset.variantId) {
        this._setVariant(checked.dataset.variantId);
      }
      return;
    }

    if (!variant || !variant.id) {
      this._setVariant(null);
      return;
    }

    // Update sticky select to match, without firing a change event.
    if (this.selectEl && this.selectEl.value !== String(variant.id)) {
      this.selectEl.value = String(variant.id);
    }

    // Update price display.
    if (this.priceEl && variant.price != null) {
      this.priceEl.textContent = this._formatMoney(variant.price);
    }

    this._setVariant(variant.id, variant.available);
  }

  _reflectToMainPicker(variantId) {
    const picker = document.querySelector('variant-picker');
    if (!picker) return;
    // Find the radio that owns this variant and click it, which
    // triggers the theme's own variant-change plumbing.
    const radios = picker.querySelectorAll('input[type="radio"][data-variant-id]');
    radios.forEach((r) => {
      if (String(r.dataset.variantId) === String(variantId) && !r.checked) {
        r.checked = true;
        r.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  _setVariant(variantId, available = true) {
    if (!this.ctaEl) return;
    if (variantId) {
      this.ctaEl.dataset.variantId = String(variantId);
      this.ctaEl.disabled = !available;
    } else {
      delete this.ctaEl.dataset.variantId;
      this.ctaEl.disabled = true;
    }
  }

  _formatMoney(cents) {
    // Shopify prices come in cents. Use the theme's money format if set,
    // otherwise a safe fallback.
    const format =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.money_format) ||
      '${{amount}}';
    const value = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*amount(_[a-z_]+)?\s*\}\}/, value);
  }

  /* ---------------- add-to-cart ---------------- */

  _bindAdd() {
    if (!this.ctaEl) return;
    this.ctaEl.addEventListener('click', (event) => this._handleAdd(event));
  }

  async _handleAdd(event) {
    event.preventDefault();
    const btn = this.ctaEl;
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.disabled) return;

    const originalText = btn.querySelector('.sticky-atc-bar__cta-label')?.textContent;
    const label = btn.querySelector('.sticky-atc-bar__cta-label');
    btn.classList.add('is-loading');
    btn.disabled = true;
    if (label) label.textContent = 'Adding…';

    // Refresh cart drawer sections after add.
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    });

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const url =
      (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) ||
      `${window.Shopify?.routes?.root || '/'}cart/add`;

    let sections = null;
    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const res = await fetch(url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      });
      try {
        const data = await res.clone().json();
        if (data && data.sections) sections = data.sections;
      } catch (_) {
        // Non-JSON response — cart-items-component will re-fetch.
      }
    } catch (err) {
      console.error('[sticky-atc-bar] network error', err);
      if (label) label.textContent = originalText || 'Add to cart';
      btn.classList.remove('is-loading');
      btn.disabled = false;
      return;
    }

    document.dispatchEvent(
      new CartAddEvent({}, 'sticky-atc-bar', {
        source: 'sticky-atc-bar',
        productId: this.dataset.productId,
        variantId,
        itemCount: 1,
        sections,
      })
    );

    // Slide out the cart drawer, matching the main ATC behaviour.
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }

    if (label) label.textContent = '✓ Added';
    setTimeout(() => {
      if (label) label.textContent = originalText || 'Add to cart';
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }, 1400);
  }
}

if (!customElements.get('sticky-atc-bar')) {
  customElements.define('sticky-atc-bar', StickyAtcBar);
}
